"use client";

import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { getCookie, setCookie } from "cookies-next";
import { saveUserData } from "@/store/apps/userData/UserDataSlice";

const TimezoneContext = createContext({
  timezone: "UTC",
  hasUserTimezone: false,
  setManualTimezone: () => {},
});

export function TimezoneProvider({ children }) {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user?.profile);
  const isAuthenticated = !!userProfile;

  const [cookieTimezone, setCookieTimezone] = useState(null);
  const [manualTimezone, setManualTimezone] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [timezoneChecked, setTimezoneChecked] = useState(false);

  // Ensure we're client-side
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Only clean manual timezone for external profile updates
    // (when profile changes but user didn't just set manual timezone)
    if (
      isAuthenticated &&
      userProfile?.time_zone &&
      manualTimezone &&
      userProfile.time_zone !== manualTimezone &&
      userProfile.time_zone !== null
    ) {
      const timer = setTimeout(() => {
        // This should now only trigger for external updates
        setManualTimezone(null);
        setCookie("user-manual-timezone", "", { maxAge: -1 });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [userProfile?.time_zone, manualTimezone, isAuthenticated]);

  // Detect timezone on mount if no cookie exists
  useEffect(() => {
    if (!mounted || timezoneChecked) return;

    const detectTimezone = async () => {
      // Check if timezone cookie already exists
      const existingTimezone = getCookie("user-timezone");

      if (existingTimezone) {
        setCookieTimezone(existingTimezone);
        setTimezoneChecked(true);
        return;
      }

      try {
        const response = await fetch("/api/utils/detect-timezone");
        const data = await response.json();

        if (data.timezone) {
          setCookieTimezone(data.timezone);
          setCookie("user-timezone", data.timezone, {
            maxAge: 30 * 24 * 60 * 60, // 30 days
            sameSite: "lax",
          });
        }
      } catch (error) {
        console.error("Timezone detection failed:", error);
      } finally {
        setTimezoneChecked(true);
      }
    };

    const timer = setTimeout(detectTimezone, 100);
    return () => clearTimeout(timer);
  }, [mounted, timezoneChecked]);

  // Check for manual timezone cookie on mount
  useEffect(() => {
    if (mounted) {
      const manualTz = getCookie("user-manual-timezone");
      if (manualTz && moment.tz.zone(manualTz)) {
        setManualTimezone(manualTz);
      }
    }
  }, [mounted]);

  const handleSetManualTimezone = async (timezone) => {
    if (!timezone || !moment.tz.zone(timezone)) {
      console.warn("Invalid timezone provided:", timezone);
      return;
    }

    // Set manual timezone immediately (for instant UI update)
    setManualTimezone(timezone);

    // Always save to cookie for persistence
    setCookie("user-manual-timezone", timezone, {
      maxAge: 30 * 24 * 60 * 60, // 30 days
      sameSite: "lax",
    });

    // For authenticated users: Save to Firestore immediately
    if (isAuthenticated && userProfile) {
      try {
        await dispatch(
          saveUserData({
            time_zone: timezone,
          })
        ).unwrap();

        // Success! Clear manual timezone since profile now has the correct value
        setManualTimezone(null);
        setCookie("user-manual-timezone", "", { maxAge: -1 });
      } catch (error) {
        console.error("Failed to save timezone to Firestore:", error);
        // Keep manual timezone as fallback if save fails
      }
    }
  };

  const { timezone, hasUserTimezone } = useMemo(() => {
    // Priority 1: Manual timezone selection (ALWAYS takes precedence)
    if (manualTimezone && moment.tz.zone(manualTimezone)) {
      return {
        timezone: manualTimezone,
        hasUserTimezone: true, // User has actively selected this
      };
    }

    // Priority 2: User profile timezone (for authenticated users)
    if (
      isAuthenticated &&
      userProfile?.time_zone &&
      moment.tz.zone(userProfile.time_zone)
    ) {
      return {
        timezone: userProfile.time_zone,
        hasUserTimezone: true,
      };
    }

    // Priority 3: Cookie timezone from previous manual selection (non-auth users)
    const manualCookie = getCookie("user-manual-timezone");
    if (manualCookie && moment.tz.zone(manualCookie)) {
      return {
        timezone: manualCookie,
        hasUserTimezone: true, // User had previously selected this
      };
    }

    // Priority 4: Cookie timezone from API detection
    if (cookieTimezone && moment.tz.zone(cookieTimezone)) {
      return {
        timezone: cookieTimezone,
        hasUserTimezone: false, // Auto-detected, not user selected
      };
    }

    // Priority 5: Browser fallback
    if (mounted && typeof window !== "undefined") {
      try {
        const guessed = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (guessed && moment.tz.zone(guessed)) {
          return {
            timezone: guessed,
            hasUserTimezone: false,
          };
        }
      } catch (error) {
        console.warn("Browser timezone detection failed:", error);
      }

      // Fallback to moment.tz.guess()
      const momentGuessed = moment.tz.guess();
      return {
        timezone: momentGuessed || "America/New_York",
        hasUserTimezone: false,
      };
    }

    return {
      timezone: "America/New_York",
      hasUserTimezone: false,
    };
  }, [
    userProfile?.time_zone,
    manualTimezone,
    cookieTimezone,
    mounted,
    isAuthenticated,
  ]);

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        hasUserTimezone,
        setManualTimezone: handleSetManualTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error("useTimezone must be used within a TimezoneProvider");
  }
  return context;
}
